import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { match, Pattern } from 'ts-pattern';

import { useRequestPasswordReset } from '~/api/auth';
import { FetchError, HttpBusinessConflictError, HttpError, HttpTooManyRequestsError } from '~/api/errors';
import { Alert, Button, ErrorMessage, ExternalLink, Link, Separator, Text } from '~/components';
import { Field, Form, Input } from '~/components/Form';
import { EMAIL } from '~/constants/regex';
import { routes } from '~/providers/RouterProvider/router.routes';
import { isNotBlank } from '~/utils/string';

import { AuthLayoutContent } from '../AuthLayout/AuthLayoutContent';
import { AuthLayoutTitleGroup } from '../AuthLayout/AuthLayoutTitleGroup';
import { ResendVerificationEmail } from '../ResendVerificationEmail/ResendVerificationEmail';
import { formStyle, loginCtaStyle, separatorStyle } from './ForgotPassword.css';

type ForgotPasswordFormFields = {
  emailAddress: string;
};

export const ForgotPassword = () => {
  const mutation = useRequestPasswordReset();

  const [autoAnimateRef] = useAutoAnimate();
  const { state } = useLocation();
  const { t } = useTranslation(['auth', 'common', 'validation']);

  const { control, handleSubmit } = useForm<ForgotPasswordFormFields>({
    defaultValues: { emailAddress: state?.passwordResetEmailAddress ?? '' },
  });

  // Snapshot of the email address input field at the time of submit,
  // which is used to resend the verification email.
  const [emailAddressSnapshot, setEmailAddressSnapshot] = useState('');

  const onSubmit = handleSubmit(({ emailAddress }) => {
    setEmailAddressSnapshot(emailAddress);
    mutation.mutate({ emailAddress });
  });

  return (
    <AuthLayoutContent>
      <AuthLayoutTitleGroup subtitle={t('auth:forgotPasswordPage.header.subtitle')} title={t('auth:forgotPasswordPage.header.title')} />

      {state?.isPasswordResetTokenExpired && !mutation.isSuccess && (
        <Alert announce icon="Warning" status="warning" title={t('auth:forgotPasswordPage.tokenExpiredAlert.title')}>
          {t('auth:forgotPasswordPage.tokenExpiredAlert.description')}
        </Alert>
      )}

      <Form className={formStyle} onSubmit={onSubmit} ref={autoAnimateRef}>
        <Controller
          control={control}
          name="emailAddress"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label>{t('auth:loginPage.form.emailField.label')}</Field.Label>
              <Input autoFocus onBlur={onBlur} onValueChange={onChange} ref={ref} type="email" value={value} />
              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{
            required: { value: true, message: t('validation:required') },
            validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
            pattern: { value: EMAIL, message: t('validation:email.invalid') },
          }}
        />

        {mutation.isSuccess && (
          <Alert announce icon="Check" status="success" title={t('auth:forgotPasswordPage.successAlert.title')}>
            {t('auth:forgotPasswordPage.successAlert.description')}
          </Alert>
        )}

        {mutation.isError &&
          match(mutation.error)
            .with(
              Pattern.instanceOf(HttpBusinessConflictError),
              (error) => error.errorCode === 'IneligibleUserCannotResetPassword',
              () => (
                <Alert announce icon="Info" status="info" title={t('auth:passwordResetErrors.ineligible.title')}>
                  {t('auth:passwordResetErrors.ineligible.description')}
                </Alert>
              ),
            )
            .with(
              Pattern.instanceOf(HttpBusinessConflictError),
              (error) => error.errorCode === 'UserWithUnverifiedEmailCannotResetPassword',
              () => (
                <Alert announce icon="Info" status="info" title={t('auth:passwordResetErrors.unverifiedEmail.title')}>
                  {t('auth:passwordResetErrors.unverifiedEmail.description')}

                  <Separator className={separatorStyle} />

                  <ResendVerificationEmail emailAddress={emailAddressSnapshot} />
                </Alert>
              ),
            )
            .with(
              Pattern.instanceOf(HttpBusinessConflictError),
              (error) => error.errorCode === 'RejectedUserCannotResetPassword',
              () => (
                <Alert announce icon="Error" status="danger" title={t('auth:passwordResetErrors.rejected.title')}>
                  {t('auth:passwordResetErrors.rejected.description')}
                </Alert>
              ),
            )
            .with(Pattern.instanceOf(HttpTooManyRequestsError), () => <ErrorMessage>{t('common:errors.tooManyRequests')}</ErrorMessage>)
            .with(Pattern.instanceOf(HttpError), () => (
              <ErrorMessage>
                <Trans components={{ email: <ExternalLink /> }} i18nKey="common:errors.httpErrorWithLink" />
              </ErrorMessage>
            ))
            .with(Pattern.instanceOf(FetchError), () => <ErrorMessage>{t('common:errors.fetchError')}</ErrorMessage>)
            .otherwise(() => <ErrorMessage>{t('common:errors.unknown')}</ErrorMessage>)}

        <Button loading={mutation.isPending} type="submit">
          {t('auth:forgotPasswordPage.form.submitButton')}
        </Button>
      </Form>

      <Separator text={t('common:or')} />

      <Text className={loginCtaStyle}>
        {t('auth:forgotPasswordPage.loginCta.text')} <Link to={routes.login}>{t('auth:forgotPasswordPage.loginCta.link')}</Link>
      </Text>
    </AuthLayoutContent>
  );
};
